import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Globe } from 'lucide-react';

const POP_COLORS = {
  afr: '#6366f1',
  amr: '#8b5cf6',
  asj: '#ec4899',
  eas: '#14b8a6',
  fin: '#f59e0b',
  nfe: '#3b82f6',
  sas: '#10b981',
  oth: '#94a3b8',
};

function formatAF(af) {
  if (af === 0) return '0';
  if (af < 0.0001) return af.toExponential(2);
  return af.toFixed(6).replace(/0+$/, '');
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl">
      <div className="font-semibold mb-1">{d.name}</div>
      <div>AF: <span className="font-mono">{formatAF(d.af)}</span></div>
      <div>AC: {d.ac} / AN: {d.an.toLocaleString()}</div>
    </div>
  );
};

export default function PopulationFrequencies({ gnomad }) {
  const chartData = gnomad.populations.map((p) => ({
    ...p,
    afDisplay: p.af * 1e6,
    label: p.code.toUpperCase(),
  }));

  const maxAF = Math.max(...gnomad.populations.map((p) => p.af));

  return (
    <section id="population" className="bg-white rounded-2xl shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background: '#021f30' }}>
        <div className="flex items-center gap-2">
          <Globe size={16} className="text-white/70" />
          <h3 className="text-white font-bold text-base">Population Frequencies</h3>
        </div>
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white border border-white/20"
          style={{ background: 'rgba(255,255,255,0.1)' }}>
          gnomAD {gnomad.version}
        </span>
      </div>

      <div className="p-6">
        {/* Global AF hero */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Global AF', value: formatAF(gnomad.global_af), sub: 'Allele Frequency', highlight: true },
            { label: 'Allele Count', value: gnomad.global_ac.toLocaleString(), sub: 'Chromosomes observed' },
            { label: 'Allele Number', value: gnomad.global_an.toLocaleString(), sub: 'Total chromosomes' },
          ].map(({ label, value, sub, highlight }) => (
            <div key={label} className={`rounded-xl p-4 border ${highlight ? 'border-coral/30' : 'border-gray-200'}`}
              style={highlight ? { background: 'rgba(249,52,65,0.05)' } : { background: '#f8fafc' }}>
              <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
              <div className={`font-extrabold font-mono text-xl ${highlight ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>

        {/* Homozygotes */}
        <div className="flex items-center gap-2 mb-5 text-sm">
          <span className="text-gray-500">Homozygotes:</span>
          <span className="font-bold text-gray-900">{gnomad.homozygotes}</span>
          {gnomad.homozygotes === 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 font-semibold">Not observed</span>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Population table */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Population Breakdown</h4>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th className="text-left px-4 py-2.5 text-gray-500 font-semibold">Population</th>
                    <th className="text-right px-3 py-2.5 text-gray-500 font-semibold">AF</th>
                    <th className="text-right px-3 py-2.5 text-gray-500 font-semibold">AC</th>
                    <th className="text-right px-3 py-2.5 text-gray-500 font-semibold">AN</th>
                    <th className="px-3 py-2.5 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {gnomad.populations.map((pop, i) => {
                    const barPct = maxAF > 0 ? (pop.af / maxAF) * 100 : 0;
                    const color = POP_COLORS[pop.code] || '#94a3b8';
                    return (
                      <tr key={pop.code} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="px-4 py-2.5 font-medium text-gray-800">{pop.name}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-gray-700">{formatAF(pop.af)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-600">{pop.ac}</td>
                        <td className="px-3 py-2.5 text-right text-gray-500">{pop.an.toLocaleString()}</td>
                        <td className="px-3 py-2.5">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden w-20">
                            <div className="h-full rounded-full" style={{ width: `${barPct}%`, background: color }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bar chart */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">AF per Population (×10⁻⁶)</h4>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="afDisplay" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell key={entry.code} fill={POP_COLORS[entry.code] || '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 text-center mt-1">Values shown as allele frequency × 10⁻⁶</p>
          </div>
        </div>
      </div>
    </section>
  );
}
